import React, { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import moment from "moment";
import { useTranslation } from "react-i18next";
import { createPortal } from "react-dom";
import IconButton from "@mui/material/IconButton";
import SweetAlert from "react-bootstrap-sweetalert";
import AssignmentIcon from "@mui/icons-material/Assignment";
import VisibilityIcon from "@mui/icons-material/Visibility";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";

function ContractOverview({ client }) {
  const [servicesDetails, setServicesDetails] = useState(null);
  const [metricsCount, setMetricsCount] = useState(0);
  const [displayCount, setDisplayCount] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const prevValue = useRef(0);
  const animationRef = useRef(null);
  const hasAnimatedRef = useRef(false);
  const { t } = useTranslation();

  useEffect(() => {
    let count = 0;
    if (client?.contractStart) count++;
    if (client?.contractExpiration) count++;
    if (client?.serviceDetail && client.serviceDetail.length > 0) {
      count += client.serviceDetail.length;
    }
    setMetricsCount(count);
  }, [client]);

  useEffect(() => {
    if (metricsCount === 0) {
      setDisplayCount(0);
      hasAnimatedRef.current = false;
      return;
    }

    const shouldAnimate = !hasAnimatedRef.current || prevValue.current !== metricsCount;

    if (!shouldAnimate) {
      setDisplayCount(metricsCount);
      return;
    }

    setIsAnimating(true);
    hasAnimatedRef.current = true;

    const startTime = Date.now();
    const startValue = prevValue.current;
    const endValue = metricsCount;
    const duration = 2000;

    const animate = () => {
      const now = Date.now();
      const elapsed = now - startTime;
      const progress = Math.min(elapsed / duration, 1);

      const easeOutQuart = 1 - Math.pow(1 - progress, 4);
      const currentValue = Math.floor(startValue + (endValue - startValue) * easeOutQuart);

      setDisplayCount(currentValue);

      if (progress < 1) {
        animationRef.current = requestAnimationFrame(animate);
      } else {
        setIsAnimating(false);
        prevValue.current = endValue;
        setTimeout(() => {
          setIsAnimating(false);
        }, 100);
      }
    };

    animationRef.current = requestAnimationFrame(animate);

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [metricsCount]);

  const handleCloseServicesDetails = () => {
    setServicesDetails(null);
    setTimeout(() => {
      document.body.classList.remove("sweet-alert-open");
      document.body.style.overflow = "";
      document.body.style.paddingRight = "";
    }, 100);
  };

  const handleOpenServicesDetails = (serviceDetails) => {
    document.body.classList.add("sweet-alert-open");
    document.body.style.overflow = "hidden";
    setServicesDetails(serviceDetails);
  };

  function getServicesDetails() {
    if (servicesDetails && servicesDetails.length > 0) {
      return (
        <div className="service-details-modal-content">
          {servicesDetails.map((el, index) => {
            const isExpired = el.expiresOn && moment(el.expiresOn).isBefore(moment());
            return (
              <div key={`service-detail-${index}`} className="service-detail-modal-item">
                <div className="service-code-section">
                  {isExpired ? (
                    <CancelIcon
                      style={{ color: "var(--red-color)", fontSize: 18, marginRight: 8 }}
                    />
                  ) : (
                    <CheckCircleIcon
                      style={{ color: "var(--green-color)", fontSize: 18, marginRight: 8 }}
                    />
                  )}
                  <span className="service-code">{el.code || ""}</span>
                </div>
                <div className="service-expiry-section">
                  <span className="expires-label">{t("user.expires_on")}</span>
                  <span className={`expiry-date ${isExpired ? "expired" : "active"}`}>
                    {el.expiresOn ? moment(el.expiresOn).format("DD/MM/YYYY") : "-"}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      );
    }
    return null;
  }

  const renderModal = () => {
    if (!servicesDetails || servicesDetails.length === 0) return null;

    return createPortal(
      <div
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 9999,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: "rgba(0, 0, 0, 0.6)",
        }}
      >
        <SweetAlert
          style={{
            display: "block",
            position: "relative",
            textAlign: "left",
            margin: 0,
            maxWidth: "90vw",
            maxHeight: "90vh",
            overflow: "hidden",
          }}
          title={t("user.service_detail")}
          onConfirm={handleCloseServicesDetails}
          onCancel={handleCloseServicesDetails}
          closeOnClickOutside
          confirmBtnText={t("ok")}
          confirmBtnCssClass="popupCloseButton"
          confirmBtnStyle={{ boxShadow: "none" }}
        >
          {getServicesDetails()}
        </SweetAlert>
      </div>,
      document.body
    );
  };

  const isContractActive =
    client?.contractExpiration && !moment(client.contractExpiration).isBefore(moment());

  return (
    <>
      {renderModal()}

      <div
        className={`user_box_container enhanced-credential-box contract-box ${metricsCount === 0 ? "empty-state" : ""}`}
      >
        <div className="box-decoration-element"></div>
        <div className="box-icon-container">
          <AssignmentIcon className="box-main-icon" />
        </div>
        <div className="user_box_title">{t("user.services")}</div>
        <div className="user_box_subtitle">{t("user.contract_and_services_overview")}</div>

        {metricsCount === 0 ? (
          <div className="empty-state-message">
            <div className="empty-state-icon">
              <AssignmentIcon />
            </div>
            <div className="empty-state-text">No contract information</div>
            <div className="empty-state-subtext">
              Contract details will appear here once configured
            </div>
          </div>
        ) : (
          <>
            <div
              className={`user_box_number_credentials_monitored ${isAnimating ? "animating" : ""}`}
            >
              {displayCount}
              <div className="number-pulse-effect"></div>
            </div>

            <div className="contract-info-section">
              {client?.contractExpiration && (
                <div className="contract-status-indicator">
                  <span className="status-label">{t("user.status")}:</span>
                  <div className="status-badge-container">
                    <span className={`status-badge ${isContractActive ? "active" : "expired"}`}>
                      <span className="status-dot"></span>
                      {isContractActive ? t("user.active") : t("user.expired")}
                    </span>
                  </div>
                </div>
              )}

              {client?.contractStart && (
                <div className="contract-date-item">
                  <span className="date-label">{t("user.contract_start")}:</span>
                  <span className="date-value">
                    {moment(client.contractStart).format("DD/MM/YYYY")}
                  </span>
                </div>
              )}

              {client?.contractExpiration && (
                <div className="contract-date-item">
                  <span className="date-label">{t("user.contract_expiration")}:</span>
                  <span className={`date-value ${isContractActive ? "active" : "expired"}`}>
                    {moment(client.contractExpiration).format("DD/MM/YYYY")}
                  </span>
                </div>
              )}
            </div>

            {client?.serviceDetail && client.serviceDetail.length > 0 && (
              <div className="box-action-container">
                <IconButton
                  onClick={() => handleOpenServicesDetails(client.serviceDetail)}
                  className="enhanced-details-button"
                  size="small"
                  disableRipple
                >
                  <VisibilityIcon className="button-icon" />
                  <span>{t("user.show_service_detail")}</span>
                </IconButton>
              </div>
            )}
          </>
        )}
      </div>
    </>
  );
}

ContractOverview.propTypes = {
  client: PropTypes.object.isRequired,
};

export default ContractOverview;
